import { api } from './client'
import type { components } from './generated/schema'
import type { StockNoteRollbackRefs } from './stock-note-ref'

// ============================================================================
// Lô sản phẩm — pattern PNK + PXK với items M-N (Phase product-lots-redesign)
// Status sống trên phiếu: pending|approved|rejected|cancelled
// ============================================================================

export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

/** 1 sản phẩm (đã gộp các lô, cộng SL) trên dòng danh sách phiếu — M-144. */
export type NoteProductSummaryLine =
    components['schemas']['NoteProductSummaryLine']

export type ProductLotInReason =
    | 'from_packaging'
    | 'from_product_packaging'
    | 'from_customer_return'
    | 'from_order_cancel'
    | 'adjustment'
    | 'from_manufacturing_partner'
    | 'from_tea_sifting'
    | 'from_agent_return'
export type ProductLotOutReason =
    'for_sale' | 'for_product_packaging' | 'discard' | 'adjustment'

export interface ProductLot {
    id: string
    code: string
    product_id: string
    product_name?: string
    product_sku?: string
    product_unit?: string
    warehouse_id: string
    warehouse_name?: string
    position_id?: string | null
    position_code?: string | null
    position_name?: string | null
    product_image_url?: string | null
    manufactured_date?: string | null
    expiry_date?: string | null
    quantity_available: number
    /** Mức CK đánh dấu của lô (0 = không đánh dấu). Ưu tiên chọn lô khi đơn CK khớp. */
    discount_percent: number
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface ProductLotStockInNoteItem {
    id: string
    note_id: string
    product_lot_id: string
    product_lot_code?: string
    product_name?: string
    product_unit?: string
    position_code?: string | null
    manufactured_date?: string | null
    expiry_date?: string | null
    product_image_url?: string | null
    quantity: number
    created_at: string
    /** Nguồn tầng dòng (reason=from_packaging): danh sách lô lấy hương nguồn + kg. */
    sources?: ProductLotStockInNoteItemSource[]
    /** Nguồn tầng dòng (reason=from_product_packaging): danh sách lô thành phẩm nguồn + kg. */
    product_sources?: ProductLotStockInNoteItemProductSource[]
    /** Vật tư đóng gói của dòng này (chặng sản phẩm, tuỳ chọn). */
    materials?: ProductLotStockInNoteItemMaterial[]
}

/** 1 lô lấy hương nguồn của 1 dòng lô sản phẩm (đọc từ API). */
export interface ProductLotStockInNoteItemSource {
    id: string
    note_item_id: string
    source_scenting_lot_id: string
    source_quantity: number
    source_scenting_lot_code?: string
}

/** 1 lô thành phẩm nguồn (túi lẻ) của 1 dòng lô sản phẩm (đọc từ API,
 *  reason=from_product_packaging). */
export interface ProductLotStockInNoteItemProductSource {
    id: string
    note_item_id: string
    source_product_lot_id: string
    source_quantity: number
    source_product_lot_code?: string
    source_product_name?: string
}

/** 1 lô vật tư + số lượng khai trên 1 dòng lô sản phẩm (đọc từ API). */
export interface ProductLotStockInNoteItemMaterial {
    id: string
    note_item_id: string
    packaging_lot_id: string
    quantity: number
    packaging_lot_code?: string
    material_name?: string
}

export interface ProductLotStockInNote extends StockNoteRollbackRefs {
    id: string
    code: string
    status: NoteStatus
    reason: ProductLotInReason
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    sales_order_id?: string | null
    order_code?: string
    stocktake_session_id?: string | null
    stocktake_session_code?: string
    scenting_lot_stock_out_note_id?: string | null
    scenting_out_note_code?: string
    packaging_lot_stock_out_note_id?: string | null
    packaging_out_note_code?: string
    /** Phiếu lấy hàng kho lấy hương đã chọn làm gợi ý (chỉ ghi lại, cho sửa). */
    scenting_lot_withdrawal_note_id?: string | null
    /** Phiếu lấy hàng kho vật tư đã chọn làm gợi ý (ghi lại, cho sửa). */
    packaging_lot_withdrawal_note_id?: string | null
    /** PXK lô thành phẩm nguồn tự sinh khi duyệt (reason=from_product_packaging). */
    product_source_out_note_id?: string
    created_at: string
    updated_at: string
    items_count?: number
    /** Chỉ API danh sách trả; phiếu không dòng thì không có khoá. */
    product_summary?: NoteProductSummaryLine[]
    items?: ProductLotStockInNoteItem[]
}

export type ProductLotStockOutNoteItem = ProductLotStockInNoteItem & {
    /** Sản phẩm của lô trên dòng này (BE join sẵn khi đọc chi tiết PXK). */
    product_id?: string
    /**
     * Trạng thái của LÔ mà dòng này trỏ tới (BE join sẵn khi đọc chi tiết PXK).
     * `virtual` = dòng NỢ LÔ: hàng đã giao khách nhưng kho chưa có lô thật, chờ
     * nhập bù (FD-372). Chỉ phiếu XUẤT mới có field này.
     */
    lot_status?: string
}

export interface ProductLotStockOutNote extends Omit<
    ProductLotStockInNote,
    'reason' | 'items'
> {
    reason: ProductLotOutReason
    items?: ProductLotStockOutNoteItem[]
}

export interface ProductLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out' | 'transfer_in' | 'transfer_out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
    /** Chỉ có với transfer_in/transfer_out — để hiện "B → C". */
    from_position_code?: string | null
    to_position_code?: string | null
    /** Chỉ có với PXK reason=for_order — mã đơn hàng + mã đơn nội bộ của đơn. */
    order_code?: string | null
    internal_order_code?: string | null
}

// ============================================================================
// Transfer notes (PDC-SP: điều chuyển lô hàng bán)
// ============================================================================

export interface ProductLotTransferNoteItem {
    id: string
    note_id: string
    source_product_lot_id: string
    source_position_id?: string | null
    quantity: number
    dest_position_id: string
    dest_product_lot_id?: string | null
    created_at: string
    // Hydrated
    product_id?: string
    product_sku?: string
    product_name?: string
    product_unit?: string
    source_product_lot_code?: string
    source_position_code?: string | null
    dest_position_code?: string | null
    dest_product_lot_code?: string | null
    manufactured_date?: string | null
    expiry_date?: string | null
    product_image_url?: string | null
    source_quantity_available?: number
}

export interface ProductLotTransferNote {
    id: string
    code: string
    status: NoteStatus
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    created_at: string
    updated_at: string
    items_count?: number
    /** Chỉ API danh sách trả; phiếu không dòng thì không có khoá. */
    product_summary?: NoteProductSummaryLine[]
    items?: ProductLotTransferNoteItem[]
}

// ============================================================================
// Request shapes
// ============================================================================

/** 1 lô vật tư + số lượng gửi lên khi tạo/sửa PNK. */
export interface ItemMaterialInput {
    packaging_lot_id: string
    quantity: number
}

/** 1 lô lấy hương nguồn gửi lên khi tạo/sửa PNK. */
export interface ItemSourceInput {
    source_lot_id: string
    source_quantity: number
}

/** Nguồn tầng dòng — chỉ gửi khi reason=from_packaging. */
interface ItemSource {
    /** Danh sách nhiều lô lấy hương nguồn. */
    sources?: ItemSourceInput[]
    /** Vật tư đóng gói của dòng (tuỳ chọn). */
    materials?: ItemMaterialInput[]
}

export type CreateInNoteItem =
    | ({ product_lot_id: string; quantity: number } & ItemSource)
    | ({
          new_lot_product_id: string
          new_lot_warehouse_id?: string
          new_lot_position_id: string
          new_lot_code?: string
          new_lot_manufactured_date?: string
          new_lot_expiry_date?: string
          /** Mức CK đánh dấu cho lô mới (0 = không đánh dấu). */
          new_lot_discount_percent?: number
          /** #M-16: id dòng sản phẩm đích của sổ đóng gói đã sinh ra dòng này.
           *  Server dùng nó để nối dòng sổ với lô vừa tạo. */
          log_output_id?: string
          quantity: number
      } & ItemSource)

export interface CreateInNoteRequest {
    reason: ProductLotInReason
    notes?: string
    items: CreateInNoteItem[]
    /**
     * Tuỳ chọn — PXK vật tư (không phải SP nào cũng tốn vật tư đóng gói).
     * (PXK lô lấy hương giờ tự sinh lúc duyệt từ nguồn tầng dòng — không gửi.)
     */
    packaging_lot_stock_out_note_id?: string
    /** Phiếu lấy hàng kho lấy hương làm gợi ý (được lưu + trừ số dư khi tạo). */
    source_withdrawal_note_id?: string
    /** Phiếu lấy hàng kho vật tư làm gợi ý (được lưu + trừ số dư khi tạo). */
    packaging_withdrawal_note_id?: string
    /**
     * Dòng sổ đóng gói 12,5g đã sinh ra phiếu này (#M-16). Có thì server gắn
     * phiếu ngược về dòng sổ ngay trong transaction tạo phiếu.
     */
    log_entry_id?: string
    /** Dòng sổ ĐÓNG HỘP đã sinh ra phiếu này (#M-16), gắn ngược y như trên. */
    box_log_entry_id?: string
    /** Dòng sổ ĐÓNG GÓI KHÁC đã sinh ra phiếu này (#M-16), gắn ngược y như trên. */
    other_log_entry_id?: string
}

export interface CreateOutNoteItemInput {
    product_lot_id: string
    quantity: number
}

export interface CreateOutNoteRequest {
    reason: ProductLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

// Sửa PNK/PXK pending: reason bất biến nhưng payload giống lúc tạo.
export type UpdateInNoteRequest = CreateInNoteRequest
export type UpdateOutNoteRequest = CreateOutNoteRequest

export interface CreateTransferItemInput {
    source_product_lot_id: string
    quantity: number
    dest_position_id: string
}

export interface CreateTransferNoteRequest {
    notes?: string
    items: CreateTransferItemInput[]
}

export type UpdateTransferNoteRequest = CreateTransferNoteRequest

export interface ListLotsParams {
    /** M-77: lọc đúng một lô — tra lô vừa quét tem ở sổ xưởng. */
    id?: string
    product_id?: string
    warehouse_id?: string
    position_id?: string
    search?: string
    only_available?: '1' | undefined
    /** '1' = chỉ lô của SP có is_packaging_source (nguồn đóng gói). */
    is_packaging_source?: '1'
    /** '1' = bỏ lô ở kệ hàng lỗi đang tắt bán (FD-397) — ô chọn lô khi xuất đơn. */
    sellable_only?: '1'
    page?: number
    per_page?: number
}

export interface ListNotesParams {
    status?: NoteStatus
    reason?: string
    sales_order_id?: string
    date_from?: string
    date_to?: string
    /** Ô tìm kiếm chung (M-149): mã phiếu, số lô, tên / SKU sản phẩm. */
    search?: string
    page?: number
    per_page?: number
}

// ============================================================================
// Lineage — truy xuất nguồn gốc 5 hop
// ============================================================================

export type LineageReason =
    | 'upstream_broken'
    | 'packaging_pnk_reason_not_from_process'
    | 'packaging_sources_missing'
    | 'scenting_process_not_found'
    | 'scenting_sources_missing'
    | 'sorting_sources_missing'
    | 'grower_missing'

export interface LineageHop<T> {
    available: boolean
    reason?: LineageReason
    data?: T
}

export interface ScentingLotSummary {
    id: string
    code: string
    aroma_material_name?: string
    warehouse_name?: string
    created_at: string
}

export interface SortingLotSummary {
    id: string
    code: string
    tea_type_name?: string
    grade?: string
    warehouse_name?: string
    created_at: string
}

export interface DriedTeaPurchaseSummary {
    id: string
    code: string
    purchase_date: string
    tea_type_name?: string
    /** Kg khô. */
    quantity: number
}

export interface FreshTeaPurchaseSummary {
    id: string
    code: string
    purchase_date: string
    /** Kg tươi. */
    quantity: number
}

export interface GrowerSummary {
    id: string
    name: string
    phone?: string
    address?: string
}

export interface ProductLotLineage {
    product_lot: {
        id: string
        code: string
        product_id: string
        product_sku: string
        product_name: string
        manufactured_date?: string
        expiry_date?: string
    }
    scenting_lots: LineageHop<ScentingLotSummary[]>
    sorting_lots: LineageHop<SortingLotSummary[]>
    // Các hop là mảng: một lô phân loại có thể lấy kg từ N phiếu cân khô.
    // Khi chỉ 1 nguồn, mảng có đúng 1 phần tử. Kho lô chè mua đã bỏ (M-27).
    dried_tea_purchases: LineageHop<DriedTeaPurchaseSummary[]>
    /** Phiếu cân tươi nguồn — mảng rỗng = phiếu mua từ hộ kinh doanh. */
    fresh_tea_purchases: LineageHop<FreshTeaPurchaseSummary[]>
    growers: LineageHop<GrowerSummary[]>
}

// ============================================================================
// API endpoints
// ============================================================================

const BASE = '/product-lots'

/**
 * Một đơn đang nợ lô sản phẩm (FD-372). `remaining` là số CÒN nợ, tức số tối đa
 * phiếu nhập hiện tại bù được cho đơn này.
 */
export interface LotDebtItem {
    id: string
    sales_order_id: string
    order_code: string
    internal_order_code: string | null
    customer_name: string
    product_id: string
    product_name: string
    product_unit: string
    quantity: number
    quantity_settled: number
    remaining: number
    created_at: string
}

export const productLotApi = {
    // Lots
    listLots: (params?: ListLotsParams) =>
        api.get<ProductLot[]>(BASE, { params }),
    /** Đơn đang nợ lô của một sản phẩm, sắp xếp bán trước bù trước. */
    listOpenLotDebts: (productId: string) =>
        api.get<LotDebtItem[]>(`${BASE}/debts`, {
            params: { product_id: productId },
        }),
    // Xuất Excel danh sách lô theo đúng filter đang áp (BE bỏ qua page/per_page,
    // tự quét toàn bộ). Trả nguyên response để đọc Content-Disposition.
    exportExcel: (params?: ListLotsParams) =>
        api.getBlob(`${BASE}/export`, { params }),
    getLot: (id: string) => api.get<ProductLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) => api.get<ProductLotHistoryEntry[]>(`${BASE}/${id}/history`, { params }),
    getLineage: (id: string) =>
        api.get<ProductLotLineage>(`${BASE}/${id}/lineage`),
    // Sửa mức CK đánh dấu của lô (sửa sau khi tạo).
    updateLotDiscount: (id: string, discountPercent: number) =>
        api.patch<{ id: string; discount_percent: number }>(
            `${BASE}/${id}/discount`,
            { discount_percent: discountPercent },
        ),
    // Sửa vị trí kho của lô.
    updateLotPosition: (id: string, positionId: string) =>
        api.patch<{ id: string; position_id: string }>(
            `${BASE}/${id}/position`,
            { position_id: positionId },
        ),

    // Stock-in notes
    listInNotes: (params?: ListNotesParams) =>
        api.get<ProductLotStockInNote[]>(`${BASE}/stock-in-notes`, { params }),
    getInNote: (id: string) =>
        api.get<ProductLotStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreateInNoteRequest) =>
        api.post<ProductLotStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdateInNoteRequest) =>
        api.put<ProductLotStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),
    /** Rollback PNK đã duyệt → BE sinh PXK điều chỉnh, trả phiếu vừa sinh. */
    rollbackInNote: (id: string) =>
        api.post<ProductLotStockOutNote>(
            `${BASE}/stock-in-notes/${id}/rollback`,
        ),

    // Stock-out notes
    listOutNotes: (params?: ListNotesParams) =>
        api.get<ProductLotStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    getOutNote: (id: string) =>
        api.get<ProductLotStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreateOutNoteRequest) =>
        api.post<ProductLotStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdateOutNoteRequest) =>
        api.put<ProductLotStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),
    /** Rollback PXK đã duyệt → BE sinh PNK điều chỉnh, trả phiếu vừa sinh. */
    rollbackOutNote: (id: string) =>
        api.post<ProductLotStockInNote>(
            `${BASE}/stock-out-notes/${id}/rollback`,
        ),

    // Transfer notes (PDC-SP)
    listTransferNotes: (params?: ListNotesParams) =>
        api.get<ProductLotTransferNote[]>(`${BASE}/transfer-notes`, {
            params,
        }),
    getTransferNote: (id: string) =>
        api.get<ProductLotTransferNote>(`${BASE}/transfer-notes/${id}`),
    createTransferNote: (req: CreateTransferNoteRequest) =>
        api.post<ProductLotTransferNote>(`${BASE}/transfer-notes`, req),
    updateTransferNote: (id: string, req: UpdateTransferNoteRequest) =>
        api.put<ProductLotTransferNote>(`${BASE}/transfer-notes/${id}`, req),
    approveTransferNote: (id: string) =>
        api.post<void>(`${BASE}/transfer-notes/${id}/approve`),
    rejectTransferNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/transfer-notes/${id}/reject`, { reason }),
    cancelTransferNote: (id: string) =>
        api.post<void>(`${BASE}/transfer-notes/${id}/cancel`),
    deleteTransferNote: (id: string) =>
        api.delete<void>(`${BASE}/transfer-notes/${id}`),
}
